import { Button, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resetUserPassword } from '@/services';

const fieldLabelProps = {
  style: { letterSpacing: '0.08em', fontSize: '0.6875rem', fontWeight: 700 },
};

interface ResetPasswordFormValues {
  newPassword: string;
}

interface ResetPasswordModalProps {
  opened: boolean;
  onClose: () => void;
  uid: string;
  displayName: string;
}

/**
 * Admin-only password reset for a driver's account. The driver is flagged
 * `mustChangePassword` server-side and forced to choose their own new
 * password on next sign-in; any existing session is revoked immediately.
 */
export const ResetPasswordModal = ({
  opened,
  onClose,
  uid,
  displayName,
}: ResetPasswordModalProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    initialValues: { newPassword: '' },
    validate: {
      newPassword: (value) =>
        value.length >= 6 ? null : t('modals.resetPassword.errors.minLength'),
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    setSubmitting(true);
    try {
      await resetUserPassword(uid, values.newPassword);
      notifications.show({
        color: 'signal',
        message: t('modals.resetPassword.notifications.success', { name: displayName }),
      });
      handleClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        message: error instanceof Error ? error.message : t('modals.resetPassword.errors.generic'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      centered
      onClose={handleClose}
      opened={opened}
      title={
        <Text c="signal.7" ff="monospace" fw={700} size="xs" style={{ letterSpacing: '0.2em' }}>
          {t('modals.resetPassword.title')}
        </Text>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text c="ink.6" size="sm">
            {t('modals.resetPassword.description', { name: displayName })}
          </Text>
          <PasswordInput
            label={t('modals.resetPassword.newPassword')}
            labelProps={fieldLabelProps}
            placeholder={t('modals.resetPassword.newPasswordPlaceholder')}
            {...form.getInputProps('newPassword')}
          />
          <Button
            fullWidth
            loading={submitting}
            style={{ boxShadow: 'none', fontWeight: 700, letterSpacing: '0.06em' }}
            type="submit"
          >
            {t('modals.resetPassword.submit')}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};
